import React, { useState } from 'react';
import {
  Typography, Paper, Button, Box, Container, Card, CardContent, FormHelperText,
} from '@mui/material';
import { Save, CheckCircle } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from '../utils/api';
import ServiceProviderFormFields from './ServiceProviderFormFields';
import {
  ServiceProviderFormState,
  ServiceProviderValidationErrors,
  emptyServiceProviderForm,
  validateServiceProviderForm,
  buildServiceProviderPayload,
  scrollServiceProviderToFirstError,
} from './serviceProviderShared';

const ServiceProviderRegistrationFormInner: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<ServiceProviderValidationErrors>({});
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [form, setForm] = useState<ServiceProviderFormState>(emptyServiceProviderForm());

  const clearError = (field: keyof ServiceProviderValidationErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async () => {
    const validationErrors = validateServiceProviderForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollServiceProviderToFirstError(validationErrors);
      setSubmitError('Please correct the highlighted fields before submitting.');
      return;
    }

    setSubmitError(null);

    if (!executeRecaptcha) {
      setSubmitError('reCAPTCHA not loaded. Please refresh the page and try again.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const recaptchaToken = await executeRecaptcha('submit');
      const payload = { ...buildServiceProviderPayload(form), recaptchaToken };
      await axios.post('/api/service-providers/register', payload);
      setRegistrationEmail(form.registrantEmail);
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to submit registration.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="md">
        <Card sx={{ mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Registration Submitted!
            </Typography>
            <Box sx={{ p: 3, textAlign: 'left', mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Verify Your Email Address
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We've sent a verification email to <strong>{registrationEmail}</strong>. Please check your Inbox and click the verification link to complete your registration.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The verification link will expire in 4 hours.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Register Your Organization
          </Typography>
          <Button
            component={Link}
            to="/register/agency/edit-request"
            variant="text"
            color="primary"
            sx={{ textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' }, whiteSpace: 'normal', textAlign: 'left' }}
          >
            Already Registered? Edit your registration here
          </Button>
        </Box>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Register your legal service organization. All fields marked with * are required.
        </Typography>

        <form onSubmit={(e) => e.preventDefault()}>
          <ServiceProviderFormFields form={form} setForm={setForm} errors={errors} clearError={clearError} />

          {submitError && (
            <Box sx={{ mt: 2 }}>
              <FormHelperText error sx={{ fontSize: '1rem' }}>{submitError}</FormHelperText>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 4,
          }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={<Save />}
              size="large"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {submitting ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

const ServiceProviderRegistrationForm: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      scriptProps={{ async: false, defer: false, appendTo: 'head', nonce: undefined }}
    >
      <ServiceProviderRegistrationFormInner />
    </GoogleReCaptchaProvider>
  );
};

export default ServiceProviderRegistrationForm;
